import { compareCancelledBeforeReturned } from "@/utils/ledgerCancellationOrder";

/**
 * Shared utility for sorting ledger entries consistently across CSV and PDF
 * generation.
 *
 * The on-screen activity list uses `compareLedgerEntries` in utils/groupByField.ts
 * instead — a different ordering (newest-first, grouped by valueDate) for a
 * different surface. The statement is oldest-first, so it puts the cancelled leg
 * of a transaction above the returned leg (chronological), while the newest-first
 * screen puts Returned above Cancelled; the two are mirror images of the same
 * reading. See utils/ledgerCancellationOrder.ts.
 */

// Generic type for ledger entries - both CSV and PDF types have these fields
interface SortableLedgerEntry {
  bookDate: string;
  uuid?: string;
  item?: number;
  reason?: string;
}

/**
 * Helper function to compare dates
 */
function compareDates(dateA: string, dateB: string): number {
  return new Date(dateA).getTime() - new Date(dateB).getTime();
}

/**
 * Helper function to compare items
 */
function compareItems(itemA?: number, itemB?: number): number {
  return (itemA ?? 0) - (itemB ?? 0);
}

/**
 * Orders the legs of a single transaction (entries sharing a uuid).
 *
 * Item number is the default, but a cancelled transaction books its cancelled
 * leg as the higher item, so item alone would render the pair as
 * Returned-then-Cancelled — reverse-chronological, inside a statement that
 * otherwise reads oldest-first.
 */
function compareWithinUuid<T extends SortableLedgerEntry>(a: T, b: T): number {
  const cancellationOrder = compareCancelledBeforeReturned(a, b);
  if (cancellationOrder !== 0) return cancellationOrder;

  return compareItems(a.item, b.item);
}

/**
 * Groups entries by UUID and sorts them appropriately
 */
function groupAndSortByUuid<T extends SortableLedgerEntry>(entries: T[]): T[] {
  const uuidGroups: Record<string, T[]> = {};
  const noUuidEntries: T[] = [];

  // Group entries by UUID
  for (const entry of entries) {
    if (entry.uuid) {
      if (!uuidGroups[entry.uuid]) {
        uuidGroups[entry.uuid] = [];
      }
      uuidGroups[entry.uuid]!.push(entry);
    } else {
      noUuidEntries.push(entry);
    }
  }

  // Sort each UUID group into leg order (item ascending, cancelled leg first)
  for (const uuid in uuidGroups) {
    uuidGroups[uuid]!.sort(compareWithinUuid);
  }

  // Combine: UUID groups first (in reverse order), then non-UUID entries
  const sortedUuidGroups = Object.values(uuidGroups).reverse();
  return [...sortedUuidGroups.flat(), ...noUuidEntries.reverse()];
}

/**
 * Sorts ledger entries with the following rules:
 * 1. Sort by bookDate in ascending order (oldest first)
 * 2. Within the same bookDate, reverse the order of entries
 * 3. For entries with the same UUID, maintain item field ascending order,
 *    except that a cancelled leg always precedes its returned counterpart
 *
 * Rule 3's exception keeps the pair chronological, matching the statement's
 * oldest-first reading (the newest-first screen mirrors it: Returned above
 * Cancelled); see utils/ledgerCancellationOrder.ts.
 *
 * @param entries - Array of ledger entries to sort
 * @returns Sorted array of entries
 */
export function sortStatementEntries<T extends SortableLedgerEntry>(
  entries: T[]
): T[] {
  // Create a copy to avoid mutating the original array
  const sortedEntries = [...entries];

  // Primary sort: by bookDate (ascending) and then by UUID and item
  sortedEntries.sort((a, b) => {
    // First, sort by bookDate (ascending - oldest first)
    const dateComparison = compareDates(a.bookDate, b.bookDate);
    if (dateComparison !== 0) {
      return dateComparison;
    }

    // If dates are the same and both have UUIDs
    if (a.uuid && b.uuid) {
      // If UUIDs are the same, sort into leg order
      if (a.uuid === b.uuid) {
        return compareWithinUuid(a, b);
      }
      // Different UUIDs - maintain stable sort order
      return a.uuid.localeCompare(b.uuid);
    }

    // If one or both don't have UUIDs, maintain original order
    return 0;
  });

  // Group entries by date
  const groupedByDate = sortedEntries.reduce(
    (groups, entry) => {
      const date = entry.bookDate;
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(entry);
      return groups;
    },
    {} as Record<string, T[]>
  );

  // Process each date group: reverse the entries within each group
  // while maintaining UUID+item ordering
  const result = Object.keys(groupedByDate)
    .sort(compareDates) // Ensure dates are in ascending order
    .flatMap((date) => {
      const group = groupedByDate[date]!;
      // Reverse the entire group first
      const reversedGroup = [...group].reverse();
      // If we need to maintain UUID+item ordering, process the group
      // Otherwise, just return the reversed group
      return groupAndSortByUuid(reversedGroup);
    });

  return result;
}
